/**
 * @file PrivateRoute.js
 * @brief Protected route component for authenticated users only
 * 
 * @details
 * Wrapper component that prevents unauthorized access to routes.
 * If user not authenticated, redirects to /login.
 * Shows loading state while checking authentication.
 * 
 * @usage
 * <PrivateRoute>
 *   <Dashboard />
 * </PrivateRoute>
 * 
 * @behavior
 * - If authenticated: Renders children component
 * - If not authenticated: Redirects to /login
 * - While checking: Shows "Loading..." message
 * 
 * @requires AuthProvider wrapper in parent app
 * @dependencies react, react-router-dom, AuthContext
 * @author FantasticTennis Team
 * @version 1.0.0
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * PrivateRoute Component
 * Redirects to login if user is not authenticated
 */
function PrivateRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-container">
        <h2>Loading...</h2>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

export default PrivateRoute;
